import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AxiosError } from 'axios';
import {
  fetchSectionSummary,
  SectionSummaryResponse,
} from 'src/api/dataVault/aiSummary';

type Status = 'idle' | 'loading' | 'success' | 'error';

interface SectionEntry {
  status: Status;
  data: SectionSummaryResponse | null;
  etag?: string;
  errorCode?: string;
  retryAfterSec?: number;
  lastFetchedAt?: number;
}

interface State {
  byKey: Record<string, SectionEntry>;
}

const initialState: State = { byKey: {} };

function makeKey(surveyId: number, sectionId: number): string {
  return `${surveyId}:${sectionId}`;
}

export const loadSectionSummary = createAsyncThunk(
  'dataVaultAiSummary/load',
  async (
    { surveyId, sectionId }: { surveyId: number; sectionId: number },
    { getState, rejectWithValue }
  ) => {
    const key = makeKey(surveyId, sectionId);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const existing = (getState() as any).dataVaultAiSummary?.byKey[key] as
      | SectionEntry
      | undefined;
    try {
      const { data, etag, status } = await fetchSectionSummary({
        surveyId,
        sectionId,
        etag: existing?.etag,
      });
      return { surveyId, sectionId, data, etag, status };
    } catch (err) {
      const axiosErr = err as AxiosError<{
        error?: string;
        retryable?: boolean;
      }>;
      const code = axiosErr.response?.data?.error ?? 'network_error';
      const retryAfter =
        Number(axiosErr.response?.headers['retry-after']) || undefined;
      return rejectWithValue({
        surveyId,
        sectionId,
        code,
        retryAfterSec: retryAfter,
        status: axiosErr.response?.status ?? 0,
      });
    }
  }
);

const slice = createSlice({
  name: 'dataVaultAiSummary',
  initialState,
  reducers: {
    invalidateSection(
      state,
      action: PayloadAction<{ surveyId: number; sectionId: number }>
    ) {
      delete state.byKey[
        makeKey(action.payload.surveyId, action.payload.sectionId)
      ];
    },
    clearAll(state) {
      state.byKey = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadSectionSummary.pending, (state, action) => {
      const key = makeKey(action.meta.arg.surveyId, action.meta.arg.sectionId);
      const prev = state.byKey[key];
      state.byKey[key] = { ...(prev ?? { data: null }), status: 'loading' };
    });
    builder.addCase(loadSectionSummary.fulfilled, (state, action) => {
      const { surveyId, sectionId, data, etag, status } = action.payload;
      const key = makeKey(surveyId, sectionId);
      const prev = state.byKey[key];
      state.byKey[key] = {
        status: 'success',
        data: status === 304 ? prev?.data ?? null : data,
        etag: etag ?? prev?.etag,
        lastFetchedAt: Date.now(),
      };
    });
    builder.addCase(loadSectionSummary.rejected, (state, action) => {
      const key = makeKey(action.meta.arg.surveyId, action.meta.arg.sectionId);
      const prev = state.byKey[key];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const payload = action.payload as any;
      state.byKey[key] = {
        ...(prev ?? { data: null }),
        status: 'error',
        errorCode: payload?.code,
        retryAfterSec: payload?.retryAfterSec,
      };
    });
  },
});

export const { invalidateSection, clearAll } = slice.actions;
export default slice.reducer;

export const selectSectionSummary =
  (surveyId: number, sectionId: number) =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (state: any): SectionEntry | undefined =>
    state.dataVaultAiSummary?.byKey[makeKey(surveyId, sectionId)];
